/* import __COLOCATED_TEMPLATE__ from './early-stage-banner.hbs'; */
/* RESPONSIBLE TEAM: team-pricing-and-packaging */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import moment from 'moment-timezone';
import type IntlService from 'ember-intl/services/intl';
import { type SafeString } from 'handlebars';
import type RouterService from '@ember/routing/router-service';
import { action } from '@ember/object';

const TRIAL = 0;

interface Args {
  appWide: boolean;
}

interface Signature {
  Element: any;
  Args: Args;
}

export default class EarlyStageBanner extends Component<Signature> {
  @service declare customerService: any;
  @service declare appService: AppService;
  @service declare permissionsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  get customer() {
    return this.customerService.customer;
  }

  get currentDate() {
    return moment.utc();
  }

  get currentEarlyStageYear(): number {
    let now = moment.utc();
    let es_step = this.customerService.earlyStageGraduation?.programOverviewData.find(
      (data: any) => now.isSameOrAfter(data.startsAt) && now.isBefore(data.endsAt),
    );

    if (!es_step) {
      return 0;
    }

    return es_step.year;
  }

  get is1MonthBeforeProgression() {
    let endDate = this.earlyStageStepEndDate(this.currentEarlyStageYear);
    let oneMonthBeforeEnd = moment.utc(endDate).subtract(1, 'months');
    return this.currentDate.isSameOrAfter(oneMonthBeforeEnd) && this.currentDate.isBefore(endDate);
  }

  programDataForYear(year: number) {
    return this.customerService.earlyStageGraduation?.programOverviewData.find(
      (data: any) => data.year === year,
    );
  }

  earlyStageStepEndDate(year: number) {
    return moment.utc(this.programDataForYear(year).endsAt);
  }

  get earlyStageBannerClassNames() {
    let currentYear = this.currentEarlyStageYear;
    let isTrial = currentYear === 0;

    return isTrial
      ? 'bg-paywall-container border-paywall-border'
      : 'bg-notice-container border-notice-border';
  }

  get showAppWideGiftBannerInstead() {
    return this.appService.app.shouldSeeUsageBasedGiftBanner && this.currentEarlyStageYear <= 1;
  }

  // shouldShowBanner
  // - this should be under the Billing Summary redesign feature flag and only show for early stage customers
  // - only show if admin is having billing permissions
  // - if cancellation is triggered before progression date, don't show this component
  // Only show during free trial and 1 month before progression
  get shouldShowBanner() {
    return (
      this.permissionsService.currentAdminCan('can_access_billing_settings') &&
      this.appService.app.canUseBillingSummaryRedesign &&
      this.customer.currentlyOnEarlyStage &&
      !this.customer.cancellationRequestedOutsideMigration &&
      this.isCorrectEarlyStageDateCriteria &&
      !this.showAppWideGiftBannerInstead
    );
  }

  /* We should not show the action button on the banner if:
  - customer is on trial
  - customer is in the update subscription page
  - customer is less than 1 day before graduation
   */
  get shouldShowBannerButton() {
    let isOnUpdateSubscriptionPage =
      this.router?.currentRouteName ===
      'apps.app.settings.subscription.billing.update-subscription.index';

    return (
      !this.isTrial &&
      !isOnUpdateSubscriptionPage &&
      !this.customerService.earlyStageGraduation?.lessThanOneDayBeforeGraduation
    );
  }

  get isCorrectEarlyStageDateCriteria() {
    return (
      (!this.customer.subscriptionCancelledAt ||
        this.customer.subscriptionCancelledAt.isAfter(
          this.earlyStageStepEndDate(this.currentEarlyStageYear),
        )) &&
      (this.currentDate.isBefore(this.earlyStageStepEndDate(TRIAL)) ||
        this.is1MonthBeforeProgression)
    );
  }

  get earlyStageBannerTranslationKey() {
    let currentYear = this.currentEarlyStageYear;
    let currentData = this.programDataForYear(currentYear);

    if (!currentData) {
      return '';
    }

    if (currentYear === 0) {
      return 'pricing-and-packaging.early_stage_banner.trial';
    }

    if (currentYear === 3) {
      if (this.customerService.earlyStageGraduation?.isGraduatingToEarlyStage) {
        return 'pricing-and-packaging.early_stage_banner.year_3';
      } else {
        return 'pricing-and-packaging.early_stage_banner.year_3_configured_subscription';
      }
    }

    return `pricing-and-packaging.early_stage_banner.year_${currentYear}`;
  }

  get earlyStageBannerTextTranslation(): string | SafeString {
    let currentYear = this.currentEarlyStageYear;
    let currentData = this.programDataForYear(currentYear);

    if (!currentData) {
      return '';
    }

    let endDate = moment.utc(currentData.endsAt);
    let formattedEndDate = endDate.format('MMMM D, YYYY');

    let nextData = this.programDataForYear(currentYear + 1);
    let translationKey = this.earlyStageBannerTranslationKey;

    return this.intl.t(translationKey, {
      discount: currentData.discount,
      nextDiscount: nextData?.discount,
      formattedEndDate,
      htmlSafe: true,
    });
  }

  get isTrial() {
    let currentYear = this.currentEarlyStageYear;
    return currentYear === 0;
  }

  get earlyStageBannerLinkTranslation() {
    if (this.isTrial) {
      return this.intl.t('pricing-and-packaging.early_stage_banner.review_your_subscription');
    } else {
      return this.intl.t('pricing-and-packaging.early_stage_banner.review_your_options');
    }
  }

  @action navigateToUpdateSubscription() {
    if (this.isTrial) {
      this.router.transitionTo('apps.app.settings.subscription.billing.subscription-details.index');
    } else {
      this.router.transitionTo('apps.app.settings.subscription.billing.update-subscription.index');
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Banners::EarlyStageBanner': typeof EarlyStageBanner;
    'banners/early-stage-banner': typeof EarlyStageBanner;
  }
}
